/**
 * Types for the Workflow Automation feature
 */

export interface Workflow {
  id: string;
  name: string;
  description?: string;
  trigger_type: string;
  trigger_conditions?: Record<string, any>;
  actions: WorkflowAction[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by?: string;
}

export interface WorkflowAction {
  type: string;
  params: Record<string, any>;
}

export interface WorkflowCreate {
  name: string;
  description?: string;
  trigger_type: string;
  trigger_conditions?: Record<string, any>;
  actions: WorkflowAction[];
  is_active: boolean;
}

export interface WorkflowUpdate {
  name?: string;
  description?: string;
  trigger_type?: string;
  trigger_conditions?: Record<string, any>;
  actions?: WorkflowAction[];
  is_active?: boolean;
}

export interface TriggerType {
  value: string;
  label: string;
}

export interface ActionParamField {
  name: string;
  label: string;
  type: 'text' | 'textarea' | 'select' | 'entity';
  required: boolean;
  options?: { value: string; label: string }[];
  placeholder?: string;
  helpText?: string;
}

export interface ActionTypeConfig {
  type: string;
  label: string;
  description: string;
  paramFields: ActionParamField[];
}

// Available trigger types
export const TRIGGER_TYPES: TriggerType[] = [
  { value: 'deal.stage.changed', label: 'Deal Stage Changed' },
  { value: 'deal.created', label: 'Deal Created' },
  { value: 'contact.created', label: 'Contact Created' },
  { value: 'task.completed', label: 'Task Completed' },
  { value: 'activity.created', label: 'Activity Created' }
];

// Available action types
export const ACTION_TYPES: ActionTypeConfig[] = [
  {
    type: 'CREATE_TASK',
    label: 'Create Task',
    description: 'Create a new task related to a deal',
    paramFields: [
      {
        name: 'description',
        label: 'Task Description',
        type: 'textarea',
        required: true,
        placeholder: 'Follow up on {deal_name}',
        helpText: 'You can use placeholders like {deal_name}, {account_name}, etc.'
      },
      {
        name: 'deal_id',
        label: 'Deal ID',
        type: 'text',
        required: false,
        helpText: 'Leave empty to use the deal from the trigger event'
      },
      {
        name: 'due_date',
        label: 'Due Date',
        type: 'text',
        required: false,
        placeholder: 'YYYY-MM-DD',
        helpText: 'Optional due date for the task'
      }
    ]
  },
  {
    type: 'SEND_EMAIL',
    label: 'Send Email',
    description: 'Send an email to a contact',
    paramFields: [
      {
        name: 'to_email',
        label: 'To Email',
        type: 'text',
        required: true,
        placeholder: '{contact_email}',
        helpText: 'You can use placeholders like {contact_email}'
      },
      {
        name: 'subject',
        label: 'Subject',
        type: 'text',
        required: true
      },
      {
        name: 'body',
        label: 'Email Body',
        type: 'textarea',
        required: true,
        helpText: 'You can use placeholders like {contact_first_name}'
      }
    ]
  },
  {
    type: 'UPDATE_FIELD',
    label: 'Update Field',
    description: 'Update a field on a record',
    paramFields: [
      {
        name: 'entity_type',
        label: 'Entity Type',
        type: 'select',
        required: true,
        options: [
          { value: 'deal', label: 'Deal' },
          { value: 'contact', label: 'Contact' },
          { value: 'account', label: 'Account' },
          { value: 'task', label: 'Task' }
        ]
      },
      {
        name: 'entity_id',
        label: 'Entity ID',
        type: 'text',
        required: false,
        helpText: 'Leave empty to use the ID from the trigger event'
      },
      {
        name: 'field_name',
        label: 'Field Name',
        type: 'text',
        required: true,
        placeholder: 'e.g., stage, name, amount'
      },
      {
        name: 'field_value',
        label: 'Field Value',
        type: 'text',
        required: true
      }
    ]
  },
  {
    type: 'POST_TO_DEAL_ROOM',
    label: 'Post to Deal Room',
    description: 'Post a message to a deal room',
    paramFields: [
      {
        name: 'deal_id',
        label: 'Deal ID',
        type: 'text',
        required: false,
        helpText: 'Leave empty to use the deal from the trigger event'
      },
      {
        name: 'message',
        label: 'Message',
        type: 'textarea',
        required: true,
        placeholder: 'Deal {deal_name} has been updated',
        helpText: 'You can use placeholders like {deal_name}, {account_name}, etc.'
      },
      {
        name: 'author_name',
        label: 'Author Name',
        type: 'text',
        required: false,
        placeholder: 'Workflow Automation',
        helpText: 'Name to display as the author of the message'
      }
    ]
  },
  {
    type: 'ASK_THE_AGENT',
    label: 'Ask the AI Agent',
    description: 'Ask the AI agent a question and use the response in subsequent steps',
    paramFields: [
      {
        name: 'prompt',
        label: 'Prompt',
        type: 'textarea',
        required: true,
        placeholder: 'Based on this deal, what is the biggest risk factor?',
        helpText: 'Question to ask the AI agent'
      },
      {
        name: 'agent_type',
        label: 'Agent Type',
        type: 'select',
        required: true,
        options: [
          { value: 'CEO_AGENT', label: 'CEO Agent' },
          { value: 'CRO_AGENT', label: 'CRO Agent' },
          { value: 'CSO_AGENT', label: 'CSO Agent' }
        ]
      },
      {
        name: 'store_as',
        label: 'Store Response As',
        type: 'text',
        required: true,
        placeholder: 'ai_response',
        helpText: 'Variable name to store the AI response for use in subsequent actions'
      }
    ]
  }
]; 